// @flow

import Mustache from "mustache";
import containerTemplate from "./container.mustache";
import megaMenuTemplate from "./mega-menu.mustache";
import iconTemplate from "./icon-menu.mustache";
import featureMenuTemplate from "./feature-menu.mustache";
import { getUserData } from "../user-data";
import type { UserData } from "../user-data/user-data-type";
import { getValue } from "../utils/getValue";

const eleMenuContainer: HTMLElement = ((document.querySelector(
    "[data-client-render-menu]"
): any): HTMLElement);

const originMenuHtml = eleMenuContainer.innerHTML;

const partial = {
    megamenu: megaMenuTemplate,
    iconmenu: iconTemplate,
    featuremenu: featureMenuTemplate
};

type MenuItem = {
    id?: string | null,
    label?: string,
    name: string,
    description?: string,
    icon?: string,
    iconSvg?: string,
    url?: string,
    permission?: string,
    visible?: boolean | string,
    feature?: boolean,
    children?: MenuItem[]
};

type MenuItemEnhanced = MenuItem & {
    idStem?: string | null,
    lvl1MenuNameEncoded?: string,
    idAttribute?: string | null,
    bold?: boolean,
    boldAndLast?: boolean,
    disabled?: boolean,
    urlAttribute?: string | null
};

type MenuType = "mega" | "tools" | "content";

type MenuItemLevel1 = {
    type: MenuType,
    title: string,
    data: MenuItem
};

type MenuItemLevel1Enhanced = MenuItemLevel1 & {
    isMegaMenu?: boolean,
    isToolMenu?: boolean,
    isContentMenu?: boolean
};

export async function render() {
    const userDataPromise: Promise<?UserData> = getUserData();
    const menuDataPromise = fetch("/site/menu");
    const menuDataArray: MenuItemLevel1[] = await (await menuDataPromise).json();
    const userData: ?UserData = await userDataPromise;
    menuDataArray.forEach(m => {
        try {
            m.data = m.data || {};
            updateLvl1MenuTypeBoolFlags(m);
            updateMenuNameAndId(m);
            updateLvl1MenuLinkUrls(m);
            updateMenuForPermissionAndVisibility(m, userData);
        } catch (e) {
            //skip for this menu
            console.error(e);
        }
    });
    try {
        updateBoldAndLast(menuDataArray);
    } catch (e) {
        //skip bolding so that we can still render the menus
        console.error(e);
    }
    const renderData = {
        userData: userData,
        children: menuDataArray
    };
    const allMenuHtml = Mustache.render(containerTemplate, renderData, partial);
    eleMenuContainer.innerHTML = `${allMenuHtml}\n${originMenuHtml}`;
}

function updateLvl1MenuTypeBoolFlags(rawMenu: MenuItemLevel1) {
    const m = ((rawMenu: any): MenuItemLevel1Enhanced);
    m.isMegaMenu = m.type === "mega";
    m.isToolMenu = m.type === "tools";
    m.isContentMenu = m.type === "content";
}

function updateLvl1MenuLinkUrls(rawMenu: MenuItemLevel1) {
    const m = ((rawMenu.data: any): MenuItemEnhanced);
    m.urlAttribute = m.url ? "href" : null;
}

function updateBoldAndLast(menuDataArray: MenuItemLevel1[]) {
    menuDataArray.forEach(
        m =>
            m.data &&
            (((m.data: any): MenuItemEnhanced).bold = m.type === "mega")
    );
    const boldMenu = menuDataArray.filter(
        m => m.data && ((m.data: any): MenuItemEnhanced).bold === true
    );
    if (boldMenu.length > 0) {
        const lastBoldMenu = boldMenu[boldMenu.length - 1];
        if (lastBoldMenu.data)
            ((lastBoldMenu.data: any): MenuItemEnhanced).boldAndLast = true;
    }
}

function updateMenuForPermissionAndVisibility(
    menu: MenuItemLevel1,
    userData: ?UserData
) {
    if (menu.data && menu.data.children) {
        menu.data.children.forEach(itemRaw => {
            const item = ((itemRaw: any): MenuItemEnhanced);
            if (item.permission) {
                const hasPermission: boolean = parsePermission(
                    item.permission
                ).some(p => getValue(userData, p) === true);
                item.disabled = hasPermission === false;
            }
            if (item.visible === undefined) {
                item.visible = true;
            } else if (typeof item.visible === "string") {
                item.visible = getValue(userData, item.visible) !== false;
            }
        });
    }
}

function parsePermission(permissionsInORlogic) {
    return permissionsInORlogic
        .split("||")
        .map(c => c.trim())
        .filter(c => c);
}

function normalizeTitle(title: string) {
    return title
        .replace(/\s/g, "-")
        .replace(/[^0-9a-zA-Z_-]/g, "")
        .split(" ")
        .join("-")
        .toLocaleLowerCase();
}

function updateMenuNameAndId(rawMenu: MenuItemLevel1) {
    //update lvl1 menu id
    const m = ((rawMenu: any): MenuItemLevel1Enhanced);
    m.data.name = m.data.name || rawMenu.title;
    m.data.id = m.data.id || `menu-${normalizeTitle(m.title)}`;
    //update sub menu id
    updateSubMenuId(m.data.children, m.type, m.data);
}

function updateSubMenuId(
    menus: ?(MenuItem[]),
    menuType: MenuType,
    lvl1Menu: MenuItem
) {
    if (menus) {
        menus.forEach(cRaw => {
            const c = ((cRaw: any): MenuItemEnhanced);
            const lvl1MenuNameEncoded = normalizeTitle(lvl1Menu.name);
            if (menuType === "mega") {
                const idValue = grabLastId(c.url);
                c.idAttribute = idValue ? "id" : null;
                c.lvl1MenuNameEncoded = lvl1MenuNameEncoded;
                c.idStem = idValue;
            } else {
                c.idAttribute = "id";
                if (!c.id) {
                    const menuTitleEncoded = normalizeTitle(c.name);
                    c.id = `menu-${lvl1MenuNameEncoded}-${menuTitleEncoded}`;
                }
            }
            if (c.children) {
                updateSubMenuId(c.children, menuType, lvl1Menu);
            }
        });
    }
}

function grabLastId(url) {
    if (!url) return null;

    const idxIndustry = url.indexOf("industryFacets=");
    const industryFacets =
        idxIndustry === -1
            ? null
            : url.substring(idxIndustry + "industryFacets=".length);

    const questionIndex = url.indexOf("?");
    if (questionIndex !== -1) {
        url = url.substring(0, questionIndex);
    }
    const idx = url.lastIndexOf("-c");

    if (idx === -1) {
        return industryFacets || null;
    } else {
        return "c" + url.substring(idx + 2);
    }
}
